// src/hooks/api/usePhases.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { phasesService } from "@/api/services/phases.service";
import type { AdvancePhaseRequest, PhaseInfo } from "@/types/api.types";

export const phaseKeys = {
  all: ["phases"] as const,
  current: () => [...phaseKeys.all, "current"] as const,
  allPhases: () => [...phaseKeys.all, "list"] as const,
  bracket: () => [...phaseKeys.all, "bracket"] as const,
  eliminated: () => [...phaseKeys.all, "eliminated"] as const,
  active: () => [...phaseKeys.all, "active"] as const,
};

export function useCurrentPhaseStats() {
  return useQuery({
    queryKey: phaseKeys.current(),
    queryFn: phasesService.getCurrentPhaseStats,
    refetchInterval: 30000,
  });
}

// ✅ useAllPhases — used by FantasyLeaderboard phase filter dropdown
export function useAllPhases() {
  return useQuery({
    queryKey: phaseKeys.allPhases(),
    queryFn: phasesService.getAllPhases as () => Promise<PhaseInfo[]>,
    staleTime: 5 * 60 * 1000,
  });
}

export function useBracket() {
  return useQuery({
    queryKey: phaseKeys.bracket(),
    queryFn: phasesService.getBracket,
  });
}

export function useEliminatedNations() {
  return useQuery({
    queryKey: phaseKeys.eliminated(),
    queryFn: phasesService.getEliminatedNations,
  });
}

export function useActiveNations() {
  return useQuery({
    queryKey: phaseKeys.active(),
    queryFn: phasesService.getActiveNations,
  });
}

export function useAdvancePhase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AdvancePhaseRequest) => phasesService.advancePhase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phaseKeys.all });
      queryClient.invalidateQueries({ queryKey: ["nations"] });
      queryClient.invalidateQueries({ queryKey: ["players"] });
    },
  });
}

export function useSimulateAutoAdvance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: phasesService.simulateAutoAdvance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phaseKeys.all });
      queryClient.invalidateQueries({ queryKey: ["nations"] });
      queryClient.invalidateQueries({ queryKey: ["players"] });
    },
  });
}
